import { Center } from '@zorro/web/design-system';
import { useId } from 'react';

import { SVGIconProps } from '../types';
import classNames from './Icon.module.scss';
import styles from './OriLogoIcon.module.scss';

/**
 * `drift` is the intro's one-shot settle; `orbit` is the looping loader. They
 * are different curves, so a single boolean cannot carry both.
 */
type OriAnimation = 'drift' | 'orbit';

type Props = SVGIconProps & { animation?: OriAnimation };

const INTRINSIC_SIZE = 24;

// The crescent is carved by offsetting an opaque ellipse over the blurred
// gradient one, so `color` has to be whatever sits behind the mark — white on a
// white card, but the surface colour anywhere else, or the knockout shows up as
// a disc.
export const OriLogoIcon = ({
  width,
  height,
  style,
  grow,
  color = 'white',
  animation,
}: Props) => {
  const gradientId = useId();
  const blurId = useId();

  return (
    <Center
      miw={width}
      mih={height}
      w={width}
      h={height}
      style={style}
      classNames={classNames}
      data-testid="ori-logo"
      data-grow={grow}
    >
      <svg
        width={width ?? INTRINSIC_SIZE}
        height={height ?? INTRINSIC_SIZE}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        overflow="visible"
      >
        <g filter={`url(#${blurId})`}>
          <ellipse
            className={styles.gradient}
            data-testid="ori-mark"
            data-animated={animation}
            cx="10.5253"
            cy="13.3605"
            rx="10.4728"
            ry="10.615"
            fill={`url(#${gradientId})`}
            fillOpacity="0.8"
          />
        </g>
        <ellipse
          cx="13.8094"
          cy="10.4295"
          rx="10.2094"
          ry="10.4295"
          fill={color}
        />
        <defs>
          {/* Figma exports this region at 2σ, which truncates the falloff into
              a visible square edge — widened to 4σ so the halo fades out. */}
          <filter
            id={blurId}
            x="-9"
            y="-6"
            width="39"
            height="39"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="BackgroundImageFix"
              result="shape"
            />
            <feGaussianBlur
              stdDeviation="1.99724"
              result="effect1_foregroundBlur"
            />
          </filter>
          <linearGradient
            id={gradientId}
            x1="0.0525"
            y1="13.3605"
            x2="20.9981"
            y2="13.3605"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#732EF7" />
            <stop offset="1" stopColor="#5AC6F1" />
          </linearGradient>
        </defs>
      </svg>
    </Center>
  );
};
